import {nextTick, reactive, watch} from 'vue'
import {Rest, Tasks} from 'utils'


import * as $utils from 'utils';
//
// IMPORTANT: if in child of BaseStore you define anything outside of _state
//            you must implement custom clone, take a look at Chat store for example
//
export default class BaseStore {
  constructor(state = {}, options = {}) {
    this._options = options
    this._changed = reactive(new Set())
    this._rest = new Rest()
    this._tasks = new Tasks()
    this._load_params = {}
    this._state = reactive({
      ...state,
      loading: false,
      loaded: false,
      saving: false,
      saved_at: undefined,
    })

    // we do not track changes for 'technical' keys added by the BaseStore
    this._do_not_track = [
      'loaded', 'loading', 'saving', 'saved_at',
      ...options.do_not_track || []
    ]

    //
    // Here we crate getters and setters for props in _state on this.
    // If there is already a getter/setter defined for the same prop on prototype then
    // we redirect call to it. So we can have custom getters and setters
    // for props in the descendants. For example
    //
    // class Strore: extends BaseStore {
    //   constructor() {
    //     super({
    //       name: 'Max',
    //       age: 22
    //     })
    //   }
    //   get name() {
    //     return 'My name: ' + this._state.name
    //   }
    // }
    //
    // So there will be 'set name()', 'get age()' and 'set age()' defined for Store
    // instances while original 'get name()' would be preserved.
    //
    const prototype = Object.getPrototypeOf(this)
    const descriptors = Object.getOwnPropertyDescriptors(prototype)
    for (const key in this._state) {
      Object.defineProperty(this, key, {
        get: function () {
          if (descriptors[key]?.get) {
            return descriptors[key].get.call(this)
          }
          return this._state[key]
        },
        set: function(value) {
          if (descriptors[key]?.set) {
            descriptors[key].set.call(this, value)
            return
          }
          this._state[key] = value
        }
      })

      //
      // Install watchers to watch for changes in _state
      // it is better cause .set in property above would
      // not track all the changes. For example when _state
      // is accessed directly by store methods or when
      // there is an array key present that requires
      // a deep watcher
      //
      // Also since we're watching a reactive object we cannot
      // watch it directly and should watch getter on property.
      // More details: https://vuejs.org/guide/essentials/watchers.html#watch-source-types
      //
      if (!this._do_not_track.includes(key)) {
        watch(
          () => this._state[key],
          () => {
            if (!this._state.loading) {
              this._changed.add(key)
            }
          },
          {deep: (this._state[key] instanceof Array)}
        )
      }
    }
  }

  async doAbort() {
  }

  async abortAll() {
    this._rest.abortAll()
    this.doAbort()
  }

  async doLoad (rest, params) {
    throw new Error('doLoad not implemented')
  }

  clone() {
    const transient = new Set([
      'loading', 'saving', 'updating'
    ])

    const state = Object.fromEntries(
      Object.entries(this._state)
        .filter(([key]) => !transient.has(key))
    )

    // Subclasses must accept their initial state as the first constructor argument.
    const cloned = new this.constructor(
      $utils.object.deepCopy(state),
      $utils.object.deepCopy(this._options)
    )

    cloned._changed = reactive(new Set(this._changed))
    return cloned
  }

  async load (params = {}, options = {}) {
    if (this._state.loading && !options.force && $utils.object.same(params, this._load_params)) {
      return
    }

    this.abortAll()
    this._state.loading = true
    this._state.loaded = false
    this._load_params = params
    try {
      await this.doLoad(this._rest, params)
    }
    finally {
      this._state.loading = false
      this._state.loaded = true
    }
  }

  get changed() {
    return this._changed.size > 0
  }

  async doSave (rest, changed) {
    throw new Error('doSave not implemented')
  }

  async save (what) {
    await nextTick()
    await this.save_now(what)
  }

  async save_now (what) {
    // if what is provided actual save is intersection between what and changed, i.e. don't save
    // something that hasn't changed
    const to_save = what ? new Set([...what].filter(item => this._changed.has(item))) : this._changed
    if (to_save.size === 0) return

    try {
      this._state.saving = true
      // false from doSave means that store
      // does not want/cannot save at the moment
      const res = await this.doSave(this._rest, to_save)
      if (res !== undefined && !res) return

      if (what && this._changed.size) {
        this._changed.clear()

        const changed = new Set([...this._changed].filter(key => !what.includes(key)))
        for(const key of changed) {
          this._changed.add(key)
        }
      }
      else {
        this._changed.clear()
      }
      this._state.saved_at = Date.now()
    }
    finally {
      this._state.saving = false
    }
  }

  _bleach(bleach) {
    if (bleach) {
      for (let key in bleach) {
        if (this._state[key] !== bleach[key]) {
          this._state[key] = bleach[key]
        }
      }
    }
  }
}
